import { apiUrl, httpClient } from "@/utilities";
import {
  DataProvider,
  GetListParams,
  GetListResult,
  RaRecord,
} from "react-admin";

const gameoo_resource = "locations";

const locationProvider: Partial<DataProvider> = {
  getList: async <RecordType extends RaRecord>(
    resource: string,
    params?: GetListParams,
  ): Promise<GetListResult<RecordType>> => {
    const filter = params?.filter ?? {};
    // ✅ Special cases for locations reference inputs
    if (resource === "locations") {
      // CASE 1: Cities by entity -> /locations/cityByEntityLocation/:entityId
      if (filter?.type === "city" && filter?.entityId) {
        const url = `${apiUrl}/locations/cityByEntityLocation/${encodeURIComponent(
          String(filter.entityId),
        )}`;

        const response = await httpClient(url);

        // backend expected: [{id,name}, ...] OR {data:[...]}
        const list = Array.isArray(response.json)
          ? response.json
          : Array.isArray(response.json?.data)
            ? response.json.data
            : [];

        return {
          data: list as RecordType[],
          total: list.length,
        };
      }

      // CASE 2: Locations by entity + city -> /locations/locationByEntityLocation?entityId&cityId
      if (filter?.entityId && filter?.cityId) {
        const url = `${apiUrl}/locations/locationByEntityLocation?${new URLSearchParams(
          {
            entityId: String(filter.entityId),
            cityId: String(filter.cityId),
          },
        ).toString()}`;

        const response = await httpClient(url);

        const list = Array.isArray(response.json)
          ? response.json
          : Array.isArray(response.json?.data)
            ? response.json.data
            : [];

        return {
          data: list as RecordType[],
          total: list.length,
        };
      }

      // If locations is requested without the needed filters (initial render)
      return { data: [] as RecordType[], total: 0 };
    }

    // ✅ Default generic behavior for all other resources
    const { page = 1, perPage = 10 } = params?.pagination ?? {};
    const { field = "", order = "" } = params?.sort ?? {};

    const query = params
      ? {
          filter: JSON.stringify(params.filter),
          range: JSON.stringify([(page - 1) * perPage, page * perPage - 1]),
          sort: JSON.stringify([field, order]),
        }
      : "";

    const url = `${apiUrl}/${resource}?${new URLSearchParams(query)}`;
    const response = await httpClient(url);

    // Ensure response has `json` and headers
    if (!response.json || !Array.isArray(response.json.data)) {
      throw new Error(
        "Invalid API response: Expected { data: [...], total: number }",
      );
    }

    return {
      data: response.json.data,
      total: response.json.total ?? response.json.data.length,
    };
  },

  getOne: async (resource, params) => {
    if (resource !== gameoo_resource) return Promise.reject();

    const queryParams = new URLSearchParams({
      id: String(params.id),
    }).toString();
    const url = `${apiUrl}/${gameoo_resource}?${queryParams}`;
    const response = await httpClient(url, { method: "GET" });

    return { data: response.json };
  },

  create: async (resource, params) => {
    if (resource !== gameoo_resource) return Promise.reject();

    const url = `${apiUrl}/${gameoo_resource}`;
    const response = await httpClient(url, {
      method: "POST",
      body: JSON.stringify(params.data),
    });

    return { data: response.json };
  },

  update: async (resource, params) => {
    if (resource !== gameoo_resource) return Promise.reject();

    const url = `${apiUrl}/${gameoo_resource}/${params.id}`;
    const response = await httpClient(url, {
      method: "PATCH",
      body: JSON.stringify(params.data),
    });

    return { data: response.json };
  },

  getMany: async (resource, params) => {
    const queryParams = new URLSearchParams({ ids: params.ids.join(",") });
    const url = `${apiUrl}/${resource}/many?${queryParams.toString()}`;
    const { json } = await httpClient(url);
    return { data: json };
  },
};

export default locationProvider;
